import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { DepositSessionResult } from '../../Casino/Type/CasinoTypes';
import { t } from '../../i18n';
import { HS_BASE_URL } from '../../Utils/SessionUrlUtils';

type DepositMessageType = 'hs.deposit.loaded' | 'hs.deposit.started' | 'hs.deposit.closed' | 'hs.deposit.resize';

type DepositMessage = {
  type: DepositMessageType;
  cashierSessionId?: string;
  width?: number;
  height?: number;
};

const DEPOSIT_MESSAGE_TYPES = new Set<DepositMessageType>([
  'hs.deposit.loaded',
  'hs.deposit.started',
  'hs.deposit.closed',
  'hs.deposit.resize',
]);

const DEFAULT_CASHIER_WIDTH = 560;
const DEFAULT_CASHIER_HEIGHT = 820;
const DEPOSIT_EMBED_READY_TIMEOUT_MS = 10_000;

interface DepositCashierModalProps {
  open: boolean;
  loading: boolean;
  error: string | null;
  session: DepositSessionResult | null;
  onClose: () => void;
}

const parseDepositMessage = (data: unknown): DepositMessage | null => {
  const parsedData = typeof data === 'string'
    ? (() => {
        try {
          return JSON.parse(data);
        } catch {
          return null;
        }
      })()
    : data;

  if (!parsedData || typeof parsedData !== 'object') return null;

  const type = (parsedData as { type?: unknown }).type;
  if (typeof type !== 'string') return null;
  if (!DEPOSIT_MESSAGE_TYPES.has(type as DepositMessageType)) return null;

  const cashierSessionId = (parsedData as { cashierSessionId?: unknown }).cashierSessionId;
  const rawWidth = (parsedData as { width?: unknown }).width;
  const rawHeight = (parsedData as { height?: unknown }).height;

  return {
    type: type as DepositMessageType,
    cashierSessionId: typeof cashierSessionId === 'string' && cashierSessionId.length > 0 ? cashierSessionId : undefined,
    width: getPositiveNumber(rawWidth),
    height: getPositiveNumber(rawHeight),
  };
};

const getPositiveNumber = (value: unknown): number | undefined => {
  const numericValue = typeof value === 'string' ? Number(value) : value;
  return typeof numericValue === 'number' && Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;
};

const getBoundedDimension = (value: number | undefined, fallback: number, min: number, max: number): number => {
  if (!value) return fallback;
  return Math.min(max, Math.max(min, Math.ceil(value)));
};

const getValidatedCashierUrl = (cashierUrl?: string | null): string | null => {
  if (!cashierUrl) return null;

  try {
    const url = new URL(cashierUrl);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.toString();
  } catch {
    return null;
  }
};

const addOriginVariants = (origins: Set<string>, rawUrl?: string | null): void => {
  if (!rawUrl) return;

  try {
    const url = new URL(rawUrl);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return;

    origins.add(url.origin);

    if (url.hostname.startsWith('www.')) {
      const withoutWww = new URL(url.toString());
      withoutWww.hostname = url.hostname.replace(/^www\./, '');
      origins.add(withoutWww.origin);
      return;
    }

    const withWww = new URL(url.toString());
    withWww.hostname = `www.${url.hostname}`;
    origins.add(withWww.origin);
  } catch {}
};

const getTrustedOrigins = (cashierUrl?: string | null): Set<string> => {
  const origins = new Set<string>();
  addOriginVariants(origins, cashierUrl);
  addOriginVariants(origins, HS_BASE_URL);
  return origins;
};

const isTrustedPokerSolutionsOrigin = (origin: string): boolean => {
  try {
    const url = new URL(origin);
    const hostname = url.hostname.toLowerCase();
    return hostname === 'pokersolutions.com' || hostname.endsWith('.pokersolutions.com');
  } catch {
    return false;
  }
};

const DepositCashierModal: React.FC<DepositCashierModalProps> = ({
  open,
  loading,
  error,
  session,
  onClose,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const cashierUrl = useMemo(() => getValidatedCashierUrl(session?.cashierUrl), [session?.cashierUrl]);
  const trustedOrigins = useMemo(() => getTrustedOrigins(cashierUrl), [cashierUrl]);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [cashierFrameLoaded, setCashierFrameLoaded] = useState(false);
  const displayedError = error || embedError;
  const showCashierFrame = Boolean(cashierUrl && !loading && !displayedError);
  const [cashierFrameSize, setCashierFrameSize] = useState<{ width?: number; height?: number } | null>(null);
  const cashierFrameWidth = getBoundedDimension(cashierFrameSize?.width, DEFAULT_CASHIER_WIDTH, 320, 900);
  const cashierFrameHeight = getBoundedDimension(cashierFrameSize?.height, DEFAULT_CASHIER_HEIGHT, 420, 1400);

  useEffect(() => {
    setCashierFrameSize(null);
    setCashierFrameLoaded(false);
    setEmbedError(null);
  }, [cashierUrl, open]);

  useEffect(() => {
    if (!open || !cashierUrl || loading || displayedError || cashierFrameLoaded) return;

    const readyTimer = window.setTimeout(() => {
      setEmbedError(t('DesktopHeader:depositFlow.unavailable'));
    }, DEPOSIT_EMBED_READY_TIMEOUT_MS);

    return () => window.clearTimeout(readyTimer);
  }, [cashierFrameLoaded, cashierUrl, displayedError, loading, open]);

  useEffect(() => {
    if (!open || trustedOrigins.size === 0) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!trustedOrigins.has(event.origin) && !isTrustedPokerSolutionsOrigin(event.origin)) return;

      const message = parseDepositMessage(event.data);
      if (!message) return;
      if (message.cashierSessionId && session?.cashierSessionId && message.cashierSessionId !== session.cashierSessionId) return;

      if (message.type === 'hs.deposit.loaded' || message.type === 'hs.deposit.started') {
        if (!message.cashierSessionId || !session?.cashierSessionId || message.cashierSessionId !== session.cashierSessionId) return;
        setCashierFrameLoaded(true);
      }

      if (message.type === 'hs.deposit.closed') {
        onClose();
        return;
      }

      if (message.type === 'hs.deposit.resize') {
        setCashierFrameSize((currentSize) => ({
          width: message.width ?? currentSize?.width,
          height: message.height ?? currentSize?.height,
        }));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onClose, open, session?.cashierSessionId, trustedOrigins]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      sx={{
        zIndex: 20000,
        '& .MuiDialog-paper': {
          width: showCashierFrame ? `min(${cashierFrameWidth}px, 100vw)` : 'min(560px, calc(100vw - 32px))',
          height: showCashierFrame ? `min(${cashierFrameHeight}px, calc(100vh - 16px))` : 'min(420px, calc(100vh - 40px))',
          maxWidth: showCashierFrame ? '100vw' : '560px',
          maxHeight: showCashierFrame ? `min(${cashierFrameHeight}px, calc(100vh - 16px))` : 'calc(100vh - 40px)',
          m: showCashierFrame ? 0 : 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: showCashierFrame ? 'transparent' : '#191A1F',
          color: 'white',
          borderRadius: showCashierFrame ? 0 : '8px',
          border: showCashierFrame ? 0 : '1px solid rgba(255,255,255,0.12)',
          boxShadow: showCashierFrame ? 'none' : undefined,
          overflow: 'hidden',
        },
        '& .MuiDialogContent-root': {
          p: 0,
          overflow: 'hidden',
          flex: '1 1 auto',
          minHeight: 0,
          width: '100%',
          height: '100%',
        },
      }}
    >
      <DialogContent>
        {loading ? (
          <Stack sx={{ height: '100%', position: 'relative' }} alignItems="center" justifyContent="center" spacing={2}>
            <IconButton
              aria-label={t('common:actions.close')}
              onClick={onClose}
              sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.72)' }}
            >
              <CloseIcon />
            </IconButton>
            <CircularProgress size={28} />
            <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14 }}>
              {t('DesktopHeader:depositFlow.loading')}
            </Typography>
          </Stack>
        ) : displayedError ? (
          <Box sx={{ p: 3, position: 'relative' }}>
            <IconButton
              aria-label={t('common:actions.close')}
              onClick={onClose}
              sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.72)' }}
            >
              <CloseIcon />
            </IconButton>
            <Alert severity="error">{displayedError}</Alert>
          </Box>
        ) : cashierUrl ? (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <iframe
              ref={iframeRef}
              title={t('DesktopHeader:depositFlow.title')}
              src={cashierUrl}
              style={{ display: 'block', width: '100%', height: '100%', border: 0 }}
              allow="payment *; clipboard-read; clipboard-write; fullscreen"
            />
            {!cashierFrameLoaded && (
              <Stack
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: '#191A1F',
                  pointerEvents: 'auto',
                }}
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <CircularProgress size={26} />
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14 }}>
                  {t('DesktopHeader:depositFlow.loading')}
                </Typography>
              </Stack>
            )}
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{t('DesktopHeader:depositFlow.unavailable')}</Alert>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DepositCashierModal;
